/**
 * P3 — N4 · Competência 3 (Geometria) · task-model K (construção)
 *
 *   "Construa a bissetriz do ângulo AÔB, com régua e compasso."
 *
 * ACHADO QUE MOTIVA O NAMESPACE `K`: o catálogo CAEd atual NÃO tem task-models de
 * construção. "Bissetriz" aparece uma única vez em 8.541 linhas — dentro de H1.15.05,
 * como objeto a ser RECONHECIDO, nunca construído. Isso não é lacuna do catálogo: ele
 * foi escrito para múltipla escolha em papel, onde construir é impossível.
 *
 * Este item usa `H1.15.K01`: o prefixo preserva a rastreabilidade até a habilidade da
 * matriz, e o `K` marca que a habilidade só se tornou avaliável porque o meio digital
 * permite construir.
 *
 * A CORREÇÃO NÃO TEM GABARITO. Não se compara a construção do aluno com uma
 * construção abençoada: verifica-se um predicado geométrico e, depois, se ele
 * SOBREVIVE À PERTURBAÇÃO DOS DADOS. Qualquer caminho válido passa; acertar no olho,
 * não.
 */

import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type {
  ConstructionSnapshot,
  GeoId,
  GeoObject,
  GLine,
  GPoint,
} from '../../geom/construction.ts';
import { addObject, EMPTY_SNAPSHOT, evaluate, geoId } from '../../geom/construction.ts';
import { isAngleBisector, isPerpBisector } from '../../geom/predicates.ts';
import { DEFAULT_SNAP } from '../../geom/snapping.ts';
import type { Tol } from '../../geom/tolerance.ts';
import { charLengthOf } from '../../geom/tolerance.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { angleAbs, dist, fromAngle, normalize, sub, toDeg, toRad } from '../../geom/vec.ts';
import { constructionToScene } from '../../render/geom-scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  CONSTR_BISSETRIZ_EXTERNA,
  CONSTR_INCOMPLETO,
  CONSTR_MEDIANA_POR_BISSETRIZ,
  CONSTR_MEDIATRIZ_POR_BISSETRIZ,
  CONSTR_OBJETO_ERRADO,
} from '../../schema/errors.ts';
import type { ConstructionTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const O_ID = geoId('O');
const A_ID = geoId('A');
const B_ID = geoId('B');
const OA_ID = geoId('lOA');
const OB_ID = geoId('lOB');

const GIVEN_IDS: ReadonlySet<string> = new Set(['O', 'A', 'B', 'lOA', 'lOB']);

export interface Params {
  /** Abertura do ângulo, em graus. */
  readonly abertura: number;
  /** Direção da bissetriz, em graus — é ela que varia a orientação espacial. */
  readonly orientacao: number;
  readonly ladoA: number;
  readonly ladoB: number;
  readonly vertice: Vec2;
}

/**
 * Restrições que impedem o item de ser resolvido por pontaria em vez de geometria:
 *
 *  - abertura entre 40° e 140° (nem raso demais nem agudo demais para construir);
 *  - a bissetriz fica a pelo menos 12° de qualquer direção "fácil" (0/45/90/135°),
 *    senão o aluno acerta alinhando pela borda da tela;
 *  - lados de comprimentos DIFERENTES, senão a mediana OA→ponto médio de AB
 *    coincide com a bissetriz e o atalho errado passaria por acaso.
 */
function drawParams(rng: Rng): Params {
  const abertura = sampleWhere(
    rng.fork('abertura'),
    (r) => r.int(40, 140),
    (a) => Math.abs(a - 90) > 6,
    { label: 'abertura' },
  );

  const orientacao = sampleWhere(
    rng.fork('orientacao'),
    (r) => r.int(0, 359),
    (ang) => {
      // Distância angular até a direção "fácil" mais próxima, em [0, 180].
      for (const facil of [0, 45, 90, 135, 180, 225, 270, 315]) {
        const d = Math.abs(((ang - facil + 180 + 360) % 360) - 180);
        if (d < 12) return false;
      }
      return true;
    },
    { label: 'orientacao' },
  );

  const ladoA = rng.int(90, 130);
  const ladoB = sampleWhere(
    rng.fork('ladoB'),
    (r) => r.int(90, 130),
    (b) => Math.abs(b - ladoA) >= 18,
    { label: 'ladoB' },
  );

  return { abertura, orientacao, ladoA, ladoB, vertice: { x: 0, y: 0 } };
}

function posicoes(p: Params): { O: Vec2; A: Vec2; B: Vec2 } {
  const meio = toRad(p.orientacao);
  const meia = toRad(p.abertura / 2);
  return {
    O: p.vertice,
    A: addVec(p.vertice, fromAngle(meio + meia, p.ladoA)),
    B: addVec(p.vertice, fromAngle(meio - meia, p.ladoB)),
  };
}

const addVec = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });

function buildGivens(p: Params): ConstructionSnapshot {
  const { O, A, B } = posicoes(p);
  let snap = EMPTY_SNAPSHOT;
  snap = addObject(snap, { id: O_ID, kind: 'point', def: { via: 'given' }, name: 'O', at: O });
  snap = addObject(snap, { id: A_ID, kind: 'point', def: { via: 'given' }, name: 'A', at: A });
  snap = addObject(snap, { id: B_ID, kind: 'point', def: { via: 'given' }, name: 'B', at: B });
  snap = addObject(snap, {
    id: OA_ID,
    kind: 'line',
    def: { via: 'through', a: O_ID, b: A_ID, span: 'segment' },
  });
  snap = addObject(snap, {
    id: OB_ID,
    kind: 'line',
    def: { via: 'through', a: O_ID, b: B_ID, span: 'segment' },
  });
  return snap;
}

// ---------------------------------------------------------------------------
// Predicado e diagnósticos
// ---------------------------------------------------------------------------

function trio(objs: ReadonlyMap<GeoId, GeoObject>): { O: GPoint; A: GPoint; B: GPoint } | null {
  const O = objs.get(O_ID);
  const A = objs.get(A_ID);
  const B = objs.get(B_ID);
  if (!O || !A || !B) return null;
  if (O.kind !== 'point' || A.kind !== 'point' || B.kind !== 'point') return null;
  return { O, A, B };
}

/**
 * Retas criadas pelo aluno (exclui as do enunciado).
 *
 * UM SEGMENTO NÃO TEM SENTIDO: o segmento de R até O é o mesmo de O até R. Ele é
 * orientado a partir da ponta mais perto do vértice, para que "parte de O" não dependa
 * da ordem dos cliques. A semirreta, essa sim, tem origem: a que parte de R e passa
 * por O não é a bissetriz.
 */
function retasDoAluno(objs: ReadonlyMap<GeoId, GeoObject>): GLine[] {
  const O = objs.get(O_ID);
  return [...objs.values()]
    .filter((o): o is GLine => o.kind === 'line' && !GIVEN_IDS.has(o.id as string))
    .map((l) =>
      !O || O.kind !== 'point' || l.span !== 'segment' || dist(l.q, O.pos) >= dist(l.p, O.pos)
        ? l
        : { ...l, p: l.q, q: l.p, dir: { x: -l.dir.x, y: -l.dir.y } },
    );
}

const alvo: ConstructionTarget = {
  describe: [
    txt('Construa a '),
    strong('bissetriz'),
    txt(' do ângulo AÔB — a semirreta que parte de O e divide o ângulo em duas partes iguais.'),
  ],

  /**
   * O corretor NUNCA olha o caminho: percorre as retas que o aluno criou e pergunta
   * a cada uma se ela é a bissetriz. Dois alunos, um pelo método dos dois arcos e
   * outro pelo losango, produzem DAGs completamente diferentes — e ambos passam.
   */
  predicate(objs, _givens, tol) {
    const t = trio(objs);
    if (!t) {
      return {
        name: 'isAngleBisector',
        ok: false,
        residual: Infinity,
        unit: 'deg',
        tolerance: tol.angleDeg,
        message: [txt('Os dados do problema não puderam ser avaliados.')],
      };
    }

    const candidatas = retasDoAluno(objs);
    if (candidatas.length === 0) {
      return {
        name: 'isAngleBisector',
        ok: false,
        residual: Infinity,
        unit: 'deg',
        tolerance: tol.angleDeg,
        message: [txt('Nenhuma reta ou semirreta foi traçada.')],
      };
    }

    let melhor = isAngleBisector(candidatas[0] as GLine, { a: t.A, vertex: t.O, b: t.B }, tol);
    for (const l of candidatas.slice(1)) {
      const r = isAngleBisector(l, { a: t.A, vertex: t.O, b: t.B }, tol);
      if (r.ok) return r;
      if (r.residual < melhor.residual) melhor = r;
    }
    return melhor;
  },

  diagnostics: [
    {
      // Construiu a mediatriz de AB — erro comum, porque a construção com dois arcos
      // é quase a mesma; muda só onde os arcos são centrados.
      error: CONSTR_MEDIATRIZ_POR_BISSETRIZ,
      test(objs, _g, tol) {
        const t = trio(objs);
        if (!t) return false;
        return retasDoAluno(objs).some((l) => isPerpBisector(l, t.A, t.B, tol).ok);
      },
    },
    {
      // Ligou o vértice ao ponto médio de AB: é a MEDIANA. Só coincide com a
      // bissetriz no triângulo isósceles — e os lados são desiguais de propósito.
      error: CONSTR_MEDIANA_POR_BISSETRIZ,
      test(objs, _g, tol) {
        const t = trio(objs);
        if (!t) return false;
        const M = { x: (t.A.pos.x + t.B.pos.x) / 2, y: (t.A.pos.y + t.B.pos.y) / 2 };
        const L = charLengthOf([t.O.pos, t.A.pos, t.B.pos]);
        return retasDoAluno(objs).some((l) => {
          if (dist(l.p, t.O.pos) / L > tol.distRel) return false;
          const dirM = normalize(sub(M, t.O.pos));
          return toDeg(angleAbs(l.dir, dirM)) <= tol.angleDeg * 3;
        });
      },
    },
    {
      // Bissetriz EXTERNA: perpendicular à interna. Passa em "ângulos iguais com as
      // duas retas suportes", e é por isso que o predicado testa o sentido.
      error: CONSTR_BISSETRIZ_EXTERNA,
      test(objs, _g, tol) {
        const t = trio(objs);
        if (!t) return false;
        const u = normalize(sub(t.A.pos, t.O.pos));
        const w = normalize(sub(t.B.pos, t.O.pos));
        const interna = normalize({ x: u.x + w.x, y: u.y + w.y });
        const externa = { x: -interna.y, y: interna.x };
        const L = charLengthOf([t.O.pos, t.A.pos, t.B.pos]);
        return retasDoAluno(objs).some((l) => {
          if (dist(l.p, t.O.pos) / L > tol.distRel) return false;
          const a = toDeg(angleAbs(l.dir, externa));
          return Math.min(a, 180 - a) <= tol.angleDeg * 3;
        });
      },
    },
    {
      error: CONSTR_OBJETO_ERRADO,
      test(objs) {
        return retasDoAluno(objs).length === 0 && objs.size > 5;
      },
    },
    {
      error: CONSTR_INCOMPLETO,
      test: () => true, // último da fila: se nada mais explicou, a construção parou no meio
    },
  ],

  robustnessTrials: 20,

  partialCredit: [
    {
      id: 'arcos',
      label: 'Traçou arcos a partir do vértice',
      weight: 0.3,
      test(objs) {
        const t = trio(objs);
        if (!t) return false;
        return [...objs.values()].some((o) => o.kind === 'circle' && dist(o.c, t.O.pos) < 1e-6);
      },
    },
    {
      id: 'intersecao',
      label: 'Marcou a interseção dos arcos',
      weight: 0.3,
      test(objs) {
        return [...objs.values()].some((o) => o.kind === 'point' && o.def.via === 'intersection');
      },
    },
    {
      id: 'semirreta',
      label: 'Traçou uma semirreta a partir do vértice',
      weight: 0.4,
      test(objs, _g, tol: Tol) {
        const t = trio(objs);
        if (!t) return false;
        const L = charLengthOf([t.O.pos, t.A.pos, t.B.pos]);
        return retasDoAluno(objs).some((l) => dist(l.p, t.O.pos) / L <= tol.distRel);
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

export const bissetriz: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('k-bissetriz'),
  version: 2,
  taskModel: taskModelCode('H1.15.K01'),
  skills: [skillCode('H1.15')],
  axis: 'II',
  level: 'N4',
  title: 'Construir a bissetriz de um ângulo',
  // NÃO É 3PL: o parâmetro de acerto ao acaso existe porque há 5 alternativas.
  // Numa construção livre, o acaso não produz uma bissetriz.
  responseModel: 'GPCM',

  difficultySources: ['construction.free-tools', 'construction.no-hint', 'steps.3plus'],

  params: drawParams,
  solve: (p) => buildGivens(p),
  // Construção não tem distratores: as ancoras de erro do N4 são os `diagnostics`.
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.abertura < 55 || p.abertura > 125) fontes.push('non-round-numbers');
    if (Math.abs(p.ladoA - p.ladoB) > 30) fontes.push('distractor-proximity.high');
    return fontes;
  },

  build(p) {
    const givens = buildGivens(p);
    const res = evaluate(givens);
    const { A, B } = posicoes(p);
    const L = charLengthOf([p.vertice, A, B]);

    const scene = constructionToScene(res, {
      title: 'Ângulo AÔB',
      desc:
        `Ângulo de vértice O com lados OA e OB, medindo ${p.abertura} graus. ` +
        'A tarefa é construir a semirreta que divide esse ângulo em duas partes iguais.',
      givenIds: GIVEN_IDS,
    });

    return {
      // Sem o aviso sobre "posicionar no olho": pedir uma CONSTRUÇÃO com régua e
      // compasso já diz que o traçado tem de ser exato. O aviso explicava o critério
      // de correção em vez de enunciar a tarefa, e chamava atenção para um atalho que
      // talvez nem tivesse ocorrido ao aluno.
      stem: [txt('A figura mostra o ângulo AÔB.')],
      comando: [
        txt('Usando as ferramentas disponíveis, construa a '),
        strong('bissetriz'),
        txt(' desse ângulo: uma semirreta, ou um segmento, com origem no vértice O.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'angulo',
          scene,
          alt: `Ângulo AÔB de ${p.abertura} graus, vértice em O.`,
        },
      ],
      interaction: {
        kind: 'construct',
        // SEM `angle-bisector` e SEM `perp-bisector`: é exatamente a ausência da
        // ferramenta que resolve em um passo que constitui a dificuldade do item
        // (fonte `construction.free-tools`).
        toolbelt: [
          'select',
          'point',
          'segment',
          'ray',
          'circle-center-through',
          'compass',
          'intersect',
          'undo',
          'delete',
        ],
        givens,
        // Perturbar A e B move os lados do ângulo. Uma construção de verdade
        // acompanha; um ponto largado a olho fica parado.
        perturbable: [
          { id: A_ID, radius: 0.14 * L },
          { id: B_ID, radius: 0.14 * L },
        ],
        target: alvo,
        snapping: { ...DEFAULT_SNAP, pixelRadius: 14 },
        // O losango: arco de centro O por A; arcos de centro A e de centro Q (onde o
        // primeiro corta OB), ambos por O; semirreta de O ao cruzamento deles.
        meta: 4,
      },
      dicas: [
        [txt('A bissetriz começa no vértice O. Falta achar um segundo ponto dela.')],
        [
          txt(
            'Um arco de centro O marca, nos dois lados do ângulo, dois pontos à mesma distância de O.',
          ),
        ],
        [
          txt(
            'Todo ponto à mesma distância desses dois pontos está na bissetriz. Dois arcos de mesmo raio, um centrado ' +
              'em cada um deles, se cruzam num ponto assim.',
          ),
        ],
      ],
      rationale: [
        strong('Construção esperada (uma entre várias válidas). '),
        txt('1) Centre o compasso em O e trace um arco que cruze os dois lados, em P e Q. '),
        txt('2) Centre em P e em Q, com a mesma abertura, e trace dois arcos que se cruzam em R. '),
        txt('3) A semirreta OR é a bissetriz.'),
        br,
        txt(
          'Qualquer caminho que produza uma semirreta partindo de O e formando ângulos iguais ' +
            'com OA e OB é aceito — inclusive o método do losango. O corretor avalia a ' +
            'propriedade geométrica, não o roteiro.',
        ),
      ],
      declared: 'media',
    };
  },
};
